import { useState } from 'react';
import { Link, useParams } from 'react-router';
import type { CopyrightEntry, FindingDetail, VulnerableSoftware } from '@ametica/shared';
import { useFindingDetail } from '../api/findings';
import { PageTitle } from '../layout/PageTitle';
import { Icon } from '../components/Icon';
import { SeverityBadge } from '../features/findings/SeverityBadge';
import { FindingHistory, FindingStatusActions } from '../features/findings/FindingStatusActions';
import { RESOLVED_REASON, SEVERITY } from '../features/findings/severity';
import { formatDateTime, timeAgo } from '../lib/time';

const DF = new Intl.DateTimeFormat('sk', { dateStyle: 'medium' });
const date = (iso: string | null) => (iso ? DF.format(new Date(iso)) : '—');

/** CVSS v3 vektor → čitateľné metriky (design/_app.js V.vuln). Neznámy kľúč sa ukáže surový. */
const CVSS_METRIC: Record<string, [string, Record<string, string>]> = {
  AV: ['Vektor útoku', { N: 'Sieť', A: 'Susedná sieť', L: 'Lokálne', P: 'Fyzicky' }],
  AC: ['Zložitosť', { L: 'Nízka', H: 'Vysoká' }],
  PR: ['Oprávnenia', { N: 'Žiadne', L: 'Nízke', H: 'Vysoké' }],
  UI: ['Interakcia', { N: 'Žiadna', R: 'Potrebná' }],
  S: ['Rozsah', { U: 'Nezmenený', C: 'Zmenený' }],
  C: ['Dôvernosť', { N: 'Žiadna', L: 'Nízka', H: 'Vysoká' }],
  I: ['Integrita', { N: 'Žiadna', L: 'Nízka', H: 'Vysoká' }],
  A: ['Dostupnosť', { N: 'Žiadna', L: 'Nízka', H: 'Vysoká' }],
};
function cvssMetrics(vector: string): Array<{ key: string; label: string; value: string }> {
  return vector.split('/').slice(1).map((part) => {
    const [k, v] = part.split(':');
    const m = CVSS_METRIC[k];
    return { key: k, label: m?.[0] ?? k, value: m?.[1][v] ?? v };
  });
}

function rangeText(r: VulnerableSoftware['ranges'][number]): string {
  const from = r.fromVersion === '*' ? '' : `${r.fromInclusive ? 'od' : 'nad'} ${r.fromVersion}`;
  const to = r.toVersion === '*' ? '' : `${r.toInclusive ? 'do' : 'pod'} ${r.toVersion}${r.toInclusive ? ' vrátane' : ''}`;
  return [from, to].filter(Boolean).join(' ') || 'všetky verzie';
}

/**
 * Detail nálezu (`/vulnerabilities/:findingId`) podľa design/_app.js V.vuln:
 * čo to je, ako je to vážne a čo s tým — plus MITRE/Defiant atribúcia, ktorá je
 * licenčná povinnosť, nie ozdoba. Zmenu stavu nálezu (ignorovať, akceptované riziko) dodá ticket 11.
 */
export function VulnerabilityPage() {
  const { findingId = '' } = useParams();
  const detail = useFindingDetail(findingId);
  const [history, setHistory] = useState(false);

  if (detail.isPending) return <PageTitle title="Detail zraniteľnosti" />;
  if (detail.isError || !detail.data) {
    return (
      <>
        <PageTitle title="Detail zraniteľnosti" actions={<Link to="/vulnerabilities" className="btn btn-g">← Späť na zoznam</Link>} />
        <div className="card p-6 text-sev-critical">Nález sa nepodarilo načítať: {detail.error?.message}</div>
      </>
    );
  }
  const { finding: f, vulnerability: v, remediation, affectedSites } = detail.data;
  const sev = SEVERITY[f.severity];
  const wordfenceRef = v.references.find((r) => r.includes('wordfence.com/threat-intel'));

  return (
    <>
      <PageTitle title="Detail zraniteľnosti" sub={v.cve ?? 'bez CVE'} actions={<Link to="/vulnerabilities" className="btn btn-g">← Späť na zoznam</Link>} />

      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex max-w-[760px] flex-col gap-3">
          <div className="flex flex-wrap gap-2.5">
            <SeverityBadge severity={f.severity} />
            {f.patchedIn
              ? <span className="pill bg-ok/15 text-ok">Patch dostupný · {f.patchedIn}</span>
              : <span className="pill bg-sev-critical/15 text-sev-critical">Bez patchu</span>}
            {f.status !== 'OPEN' && (
              <span className="pill bg-raised text-faint">
                {f.status === 'RESOLVED' ? `uzavretý — ${f.resolvedReason ? RESOLVED_REASON[f.resolvedReason] : ''}` : f.status === 'IGNORED' ? 'ignorovaný' : 'akceptované riziko'}
              </span>
            )}
            {v.informational && <span className="pill bg-raised text-faint">informatívny záznam</span>}
          </div>
          <h1 className="m-0 text-[23px] font-semibold leading-[1.35] tracking-[-.4px] text-balance">{v.title}</h1>
          <span className="mono text-[12.5px] text-faint">
            {f.component.name} · {f.component.slug} {f.installedVersion} · <Link to={`/sites/${f.siteId}`}>{f.site.name}</Link> ({f.site.host})
          </span>
        </div>
        <div className="flex gap-2.5">
          <FindingStatusActions findingId={f.id} status={f.status} historyOpen={history} onToggleHistory={() => setHistory((h) => !h)} />
          {wordfenceRef && (
            <a href={wordfenceRef} target="_blank" rel="noreferrer" className="btn btn-g">
              Otvoriť vo Wordfence <Icon name="out" size={13} />
            </a>
          )}
        </div>
      </div>

      {history && (
        <div className="card px-5 py-4">
          <span className="card-t">História nálezu</span>
          <FindingHistory findingId={f.id} />
        </div>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(300px,380px)]">
        <div className="flex flex-col gap-[22px]">
          <div className="card grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-[18px] px-5 py-[18px]">
            <Kv k="CVE" v={v.cve ? (v.cveLink ? <a href={v.cveLink} target="_blank" rel="noreferrer">{v.cve}</a> : v.cve) : 'nepridelené'} />
            <Kv k="CVSS 3.x" v={v.cvss?.score != null ? `${v.cvss.score.toFixed(1)} · ${v.cvss.rating ?? sev.label}` : `— · ${sev.label}`} cls={v.cvss?.score != null ? sev.text : 'text-faint'} />
            <Kv k="CWE" v={v.cwe ? `CWE-${v.cwe.id}` : '—'} title={v.cwe?.name ?? undefined} />
            <Kv k="Zverejnené" v={date(v.published)} />
            <Kv k="Aktualizované" v={date(v.updated)} />
          </div>

          <Section title="Popis">
            <p className="m-0 max-w-[68ch] text-[13.5px] leading-[1.7] text-dim whitespace-pre-line">
              {v.description ?? (v.sources.production ? 'Wordfence k tomuto záznamu neuvádza popis.' : 'Záznam je zatiaľ len v scanner feede — popis, CVSS a odporúčanie pribudnú po synchronizácii production feedu.')}
            </p>
            {v.cwe?.name && <div className="text-xs text-faint">CWE-{v.cwe.id}: {v.cwe.name}</div>}
          </Section>

          {v.cvss?.vector && (
            <Section title="CVSS vektor">
              <code className="mono text-[11.5px] text-faint break-all">{v.cvss.vector}</code>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-2">
                {cvssMetrics(v.cvss.vector).map((m) => (
                  <div key={m.key} className="rounded-[var(--radius-chip)] border border-line bg-surface px-[11px] py-[9px]">
                    <div className="mono text-[10.5px] text-faint">{m.key} · {m.label}</div>
                    <div className="mt-[3px] text-[12.5px]">{m.value}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title="Zasiahnuté verzie">
            {v.software.map((s) => (
              <div key={`${s.type}:${s.slug}`} className={`card flex flex-wrap items-center gap-3.5 px-[18px] py-3.5 ${s.slug === f.component.slug && s.type === f.component.type ? '' : 'opacity-70'}`}>
                <span className="text-[13px]">{s.name} <span className="mono text-xs text-faint">{s.slug} · {s.type}</span></span>
                {s.ranges.map((r) => <span key={r.label} className={`mono text-[13px] ${sev.text}`}>{rangeText(r)}</span>)}
                {s.patchedVersions.length > 0 && (
                  <>
                    <Icon name="chev" size={15} className="text-ghost" />
                    <span className="mono text-[13px] text-ok">{s.patchedVersions.join(', ')}</span>
                    <span className="text-[11.5px] text-ghost">opravené</span>
                  </>
                )}
                {s.patchedVersions.length === 0 && <span className="text-[11.5px] text-ghost">bez opravenej verzie</span>}
              </div>
            ))}
          </Section>

          <div className={`note ${f.patchedIn ? 'border-ok/25 bg-ok/8' : 'border-sev-critical/25 bg-sev-critical/8'}`}>
            <Icon name={f.patchedIn ? 'up' : 'warn'} size={17} className={`mt-px shrink-0 ${f.patchedIn ? 'text-ok' : 'text-sev-critical'}`} />
            <div>
              <div className="mb-1 text-[13px] font-semibold">Odporúčaná náprava</div>
              <div className="text-[13px] leading-[1.6] text-dim">
                {f.patchedIn
                  ? <>Aktualizuj <span className="mono text-ink">{f.component.slug}</span> z verzie <span className="mono text-ink">{f.installedVersion}</span> na <span className="mono text-ok">{f.patchedIn}</span> alebo novšiu.</>
                  : 'Opravená verzia zatiaľ nie je známa. Zváž dočasné vypnutie komponentu alebo obmedzenie prístupu.'}
                {remediation && <div className="mt-1.5 text-xs text-faint">Wordfence: {remediation}</div>}
              </div>
            </div>
          </div>

          {v.references.length > 0 && (
            <Section title="Referencie">
              <ul className="m-0 flex list-none flex-col gap-1 p-0 text-xs">
                {v.references.map((r) => <li key={r}><a href={r} target="_blank" rel="noreferrer" className="break-all">{r}</a></li>)}
              </ul>
              {v.researchers.length > 0 && <div className="text-xs text-faint">Nahlásili: {v.researchers.join(', ')}</div>}
            </Section>
          )}
        </div>

        <div className="flex flex-col gap-[22px]">
          <div className="card flex flex-col gap-3 px-5 py-[18px]">
            <div className="flex items-baseline justify-between">
              <span className="card-t">Zasiahnuté stránky</span>
              <span className="mono text-[11.5px] text-faint">{affectedSites.length}</span>
            </div>
            {affectedSites.map((s) => (
              <Link key={s.findingId} to={`/vulnerabilities/${s.findingId}`} className={`flex items-center justify-between gap-2 py-1 text-ink hover:text-ink ${s.findingId === f.id ? '' : 'opacity-80'}`}>
                <span className="flex items-center gap-2.5">
                  <span className={`dotst ${s.status === 'OPEN' ? sev.bg : 'bg-ghost'}`} />
                  <span>
                    <span className="text-[13px]">{s.name}</span>
                    <span className="sub block">{s.host} · {s.installedVersion} · {s.status === 'OPEN' ? 'otvorený' : s.status === 'RESOLVED' ? 'uzavretý' : s.status === 'IGNORED' ? 'ignorovaný' : 'akceptované riziko'}</span>
                  </span>
                </span>
                <Icon name="chev" size={13} className="text-ghost" />
              </Link>
            ))}
          </div>

          <div className="card flex flex-col gap-[9px] px-5 py-[18px]">
            <span className="card-t mb-[3px]">Detekcia</span>
            <Kv2 k="Prvýkrát zistené" v={formatDateTime(f.firstDetectedAt)} />
            <Kv2 k="Naposledy videné" v={timeAgo(f.lastSeenAt)} />
            {f.resolvedAt && <Kv2 k="Uzavreté" v={formatDateTime(f.resolvedAt)} />}
            <Kv2 k="Zdroj" v={v.sources.production ? 'production feed' : 'scanner feed'} />
            <Kv2 k="ID záznamu" v={<span className="break-all">{v.id}</span>} />
          </div>

          <Attribution copyrights={v.copyrights} />
        </div>
      </div>
    </>
  );
}

/** Autorské práva — licenčná podmienka Wordfence Intelligence a CVE®: viditeľné pri každom zázname. */
function Attribution({ copyrights }: { copyrights: FindingDetail['vulnerability']['copyrights'] }) {
  const entries: CopyrightEntry[] = copyrights.entries.length > 0 ? copyrights.entries : [
    { notice: 'Copyright Defiant Inc. — Wordfence Intelligence', license: '', licenseUrl: 'https://www.wordfence.com/wordfence-intelligence-terms-and-conditions/' },
    { notice: 'Copyright The MITRE Corporation — CVE®', license: '', licenseUrl: 'https://www.cve.org/Legal/TermsOfUse' },
  ];
  return (
    <section className="card flex flex-col gap-2 px-5 py-4" aria-label="Autorské práva">
      <div className="flex items-center gap-2">
        <Icon name="info" size={13} className="text-faint" />
        <span className="text-[10.5px] font-semibold tracking-[.4px] text-dim">AUTORSKÉ PRÁVA</span>
      </div>
      <p className="m-0 text-[10.5px] leading-[1.6] text-faint">{copyrights.message ?? 'Tento záznam obsahuje materiál chránený autorským právom.'}</p>
      {entries.map((c) => (
        <p key={c.notice} className="m-0 text-[10.5px] leading-[1.6] text-faint" title={c.license || undefined}>
          {c.notice}
          {c.licenseUrl && <> — <a href={c.licenseUrl} target="_blank" rel="noreferrer" className="text-[10.5px]">licenčné podmienky</a></>}
        </p>
      ))}
    </section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-[11px]">
      <span className="card-t">{title}</span>
      {children}
    </div>
  );
}

function Kv({ k, v, cls = 'text-ink', title }: { k: string; v: React.ReactNode; cls?: string; title?: string }) {
  return (
    <div className="flex flex-col gap-[5px]" title={title}>
      <span className="text-[10.5px] tracking-[.4px] text-faint">{k}</span>
      <span className={`mono text-[13.5px] ${cls}`}>{v}</span>
    </div>
  );
}

function Kv2({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-xs text-faint">{k}</span>
      <span className="mono text-right text-xs text-dim">{v}</span>
    </div>
  );
}
